import { InlineFeedback } from '../feedback/index.js';
import React from 'react';
import clsx from 'clsx';

const Input = React.forwardRef(({
  type = 'text',
  size = 'md',
  variant = 'default',
  error = false,
  disabled = false,
  className,
  label,
  inlineLabel = false,
  inFieldLabel = false,
  helperText,
  errorMessage,
  startIcon,
  endIcon,
  inFieldStatus,
  showCharacterCount = false,
  inFieldCharacterCount = false,
  maxCharacters,
  ...props
}, ref) => {
  const generatedId = React.useId();
  const inputId = props.id || generatedId;
  const helperTextId = `${inputId}-helper-text`;
  const errorTextId = `${inputId}-error-text`;
  const currentLength = typeof props.value === 'string'
    ? props.value.length
    : typeof props.value === 'number'
      ? String(props.value).length
      : 0;
  const shouldShowCharacterCount = showCharacterCount && Number.isFinite(maxCharacters);
  const shouldShowInFieldCount = shouldShowCharacterCount && inFieldCharacterCount;
  const shouldShowExternalCount = shouldShowCharacterCount && !inFieldCharacterCount;
  const characterCountVisible = shouldShowCharacterCount && maxCharacters > 0 && currentLength / maxCharacters >= 0.8;
  const hasInFieldLabel = Boolean(label && inFieldLabel);
  const describedBy = [
    error && errorMessage ? errorTextId : null,
    !error && helperText ? helperTextId : null,
  ].filter(Boolean).join(' ') || undefined;
  const hasWrapper = Boolean(label || helperText || (error && errorMessage));

  const baseClasses = 'block w-full border rounded-md shadow-sm transition-colors focus:outline-none focus:ring-1';

  const variants = {
    default: 'border-gray-300 focus:border-[var(--primary,#1D4ED8)] focus:ring-[var(--primary,#1D4ED8)]',
    filled: 'border-gray-300 bg-gray-50 focus:bg-white focus:border-[var(--primary,#1D4ED8)] focus:ring-[var(--primary,#1D4ED8)]'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const errorClasses = error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : '';
  const disabledClasses = disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '';

  const inputClasses = clsx(
    baseClasses,
    variants[variant],
    sizes[size],
    errorClasses,
    disabledClasses,
    hasInFieldLabel && 'pt-5 pb-2',
    startIcon && 'pl-10',
    hasInFieldLabel && startIcon && 'pl-10',
    shouldShowInFieldCount && 'pr-14',
    endIcon && 'pr-10',
    className
  );

  const inputElement = (
    <div
      className="relative"
      data-otg-component={!hasWrapper ? (props['data-otg-component'] || 'Input') : undefined}
    >
      {hasInFieldLabel ? (
        <label
          htmlFor={inputId}
          className="pointer-events-none absolute left-3 top-2 z-[1] text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--primary,#1D4ED8)]"
        >
          {label}
        </label>
      ) : null}
      {inFieldStatus ? (
        <div
          className="pointer-events-none absolute right-3 top-2 z-[1] text-[11px] font-medium text-success"
          aria-live="polite"
        >
          {inFieldStatus}
        </div>
      ) : null}
      {startIcon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <span className="text-gray-500 sm:text-sm">
            {startIcon}
          </span>
        </div>
      )}
      <input
        id={inputId}
        ref={ref}
        type={type}
        className={inputClasses}
        disabled={disabled}
        aria-invalid={error}
        aria-describedby={describedBy}
        maxLength={Number.isFinite(maxCharacters) ? maxCharacters : props.maxLength}
        {...props}
      />
      {shouldShowInFieldCount ? (
        <div
          className={clsx(
            'pointer-events-none absolute bottom-2 right-3 text-[11px] font-medium text-gray-500 transition-opacity duration-200',
            characterCountVisible ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden={characterCountVisible ? 'false' : 'true'}
        >
          {currentLength}/{maxCharacters}
        </div>
      ) : null}
      {endIcon && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-gray-500 sm:text-sm">
            {endIcon}
          </span>
        </div>
      )}
    </div>
  );

  if (hasWrapper) {
    return (
      <div data-otg-component={props['data-otg-component'] || 'Input'}>
        {!hasInFieldLabel && (label || shouldShowExternalCount) ? (
          <div className={clsx('mb-1', inlineLabel || shouldShowCharacterCount ? 'flex items-center justify-between gap-3' : null)}>
            {label ? (
              <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
                {label}
              </label>
            ) : <span />}
            {shouldShowExternalCount ? (
              <span className="text-xs font-medium text-gray-500">
                {currentLength}/{maxCharacters}
              </span>
            ) : null}
          </div>
        ) : null}
        {inputElement}
        {error && errorMessage && (
          <InlineFeedback variant="error" id={errorTextId} className="mt-1 text-sm text-red-600">
            {errorMessage}
          </InlineFeedback>
        )}
        {!error && helperText && (
          <p id={helperTextId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }

  return inputElement;
});

Input.displayName = 'Input';

// Textarea variant
const Textarea = React.forwardRef(({
  size = 'md',
  variant = 'default',
  error = false,
  disabled = false,
  className,
  label,
  inlineLabel = false,
  inFieldLabel = false,
  helperText,
  errorMessage,
  rows = 4,
  showCharacterCount = false,
  inFieldCharacterCount = false,
  maxCharacters,
  inFieldStatus,
  ...props
}, ref) => {
  const generatedId = React.useId();
  const textareaId = props.id || generatedId;
  const helperTextId = `${textareaId}-helper-text`;
  const errorTextId = `${textareaId}-error-text`;
  const currentLength = typeof props.value === 'string'
    ? props.value.length
    : typeof props.value === 'number'
      ? String(props.value).length
      : 0;
  const shouldShowCharacterCount = showCharacterCount && Number.isFinite(maxCharacters);
  const shouldShowInFieldCount = shouldShowCharacterCount && inFieldCharacterCount;
  const shouldShowExternalCount = shouldShowCharacterCount && !inFieldCharacterCount;
  const characterCountVisible = shouldShowCharacterCount && maxCharacters > 0 && currentLength / maxCharacters >= 0.8;
  const hasInFieldLabel = Boolean(label && inFieldLabel);
  const describedBy = [
    error && errorMessage ? errorTextId : null,
    !error && helperText ? helperTextId : null,
  ].filter(Boolean).join(' ') || undefined;
  const hasWrapper = Boolean(label || helperText || (error && errorMessage));

  const baseClasses = 'block w-full border rounded-md shadow-sm transition-colors focus:outline-none focus:ring-1 resize-vertical';

  const variants = {
    default: 'border-gray-300 focus:border-[var(--primary,#1D4ED8)] focus:ring-[var(--primary,#1D4ED8)]',
    filled: 'border-gray-300 bg-gray-50 focus:bg-white focus:border-[var(--primary,#1D4ED8)] focus:ring-[var(--primary,#1D4ED8)]'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2 text-sm',
    lg: 'px-4 py-3 text-base'
  };

  const errorClasses = error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : '';
  const disabledClasses = disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : '';

  const textareaClasses = clsx(
    baseClasses,
    variants[variant],
    sizes[size],
    errorClasses,
    disabledClasses,
    hasInFieldLabel && 'pt-5 pb-2',
    shouldShowInFieldCount && 'pb-6',
    className
  );

  const textareaElement = (
    <div
      className="relative"
      data-otg-component={!hasWrapper ? (props['data-otg-component'] || 'Textarea') : undefined}
    >
      {hasInFieldLabel ? (
        <label
          htmlFor={textareaId}
          className="pointer-events-none absolute left-3 top-2 z-[1] text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--primary,#1D4ED8)]"
        >
          {label}
        </label>
      ) : null}
      {inFieldStatus ? (
        <div
          key={inFieldStatus}
          className="pointer-events-none absolute right-3 top-2 z-[1] text-[11px] font-medium text-success animate-saved-indicator"
          aria-live="polite"
        >
          {inFieldStatus}
        </div>
      ) : null}
      <textarea
        id={textareaId}
        ref={ref}
        rows={rows}
        className={textareaClasses}
        disabled={disabled}
        aria-invalid={error}
        aria-describedby={describedBy}
        maxLength={Number.isFinite(maxCharacters) ? maxCharacters : props.maxLength}
        {...props}
      />
      {shouldShowInFieldCount ? (
        <div
          className={clsx(
            'pointer-events-none absolute bottom-2 right-3 text-[11px] font-medium text-gray-500 transition-opacity duration-200',
            characterCountVisible ? 'opacity-100' : 'opacity-0'
          )}
          aria-hidden={characterCountVisible ? 'false' : 'true'}
        >
          {currentLength}/{maxCharacters}
        </div>
      ) : null}
    </div>
  );

  if (hasWrapper) {
    return (
      <div data-otg-component={props['data-otg-component'] || 'Textarea'}>
        {!inFieldLabel && (label || shouldShowExternalCount) ? (
          <div className={clsx('mb-1', inlineLabel || shouldShowCharacterCount ? 'flex items-center justify-between gap-3' : null)}>
            {label ? (
              <label htmlFor={textareaId} className="block text-sm font-medium text-gray-700">
                {label}
              </label>
            ) : <span />}
            {shouldShowExternalCount ? (
              <span className="text-xs font-medium text-gray-500">
                {currentLength}/{maxCharacters}
              </span>
            ) : null}
          </div>
        ) : null}
        {textareaElement}
        {error && errorMessage && (
          <InlineFeedback variant="error" id={errorTextId} className="mt-1 text-sm text-red-600">
            {errorMessage}
          </InlineFeedback>
        )}
        {!error && helperText && (
          <p id={helperTextId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }

  return textareaElement;
});

Textarea.displayName = 'Textarea';

Input.Textarea = Textarea;

export { Textarea };
export default Input;
